import React from 'react';
import { Eye, MessageCircle, Zap, TrendingUp } from 'lucide-react';

const HypeArticleCard = ({ article, featured = false }) => {
  // Don't show view counter to avoid looking fake
  const isHot = article.wow_factor >= 9 || (article.performance_metrics?.views > 1000);
  
  return (
    <div 
      className={`card-neon overflow-hidden group cursor-pointer ${
        featured ? 'md:col-span-2 md:row-span-2' : ''
      }`}
    >
      {/* Image */}
      <div className={`relative overflow-hidden ${
        featured ? 'h-96' : 'h-48'
      }`}>
        <img
          src={article.featured_image || article.image || 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800'}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />
        
        {/* Hot Badge */}
        {isHot && (
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-gradient-to-r from-[#ff0080] to-[#cc00ff] text-white px-4 py-2 rounded-full font-bold text-xs uppercase neon-flicker">
            <Flame className="w-4 h-4" />
            <span>HOT</span>
          </div>
        )}
        
        {/* Category Badge */}
        <div className="absolute top-4 right-4 bg-[#00ff88] text-[#0a0a0b] px-4 py-1 rounded-full font-bold text-xs uppercase">
          {article.category}
        </div>
        
        {/* Bottom Info */}
        <div className="absolute bottom-4 left-4 right-4">
          <h3 
            className={`font-black text-white mb-2 line-clamp-2 ${
              featured ? 'text-3xl' : 'text-xl'
            }`}
            style={{fontFamily: 'Space Grotesk, sans-serif'}}
          >
            {article.title}
          </h3>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6">
        <p className="text-[#b4b4b9] mb-4 line-clamp-2">
          {article.excerpt}
        </p>
        
        {/* Author & Stats */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {article.author?.avatar && (
              <img
                src={article.author.avatar}
                alt={article.author.name}
                className="w-10 h-10 rounded-full border-2 border-[#00ff88]"
              />
            )}
            <div>
              <div className="font-bold text-white text-sm">
                {article.author?.name || article.persona}
              </div>
              <div className="text-xs text-[#6b6b73]">
                {article.published_at ? 
                  new Date(article.published_at).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  }) : 
                  'Recent'
                }
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4 text-[#6b6b73]">
            {/* Removed view counter - looks fake with 0 views */}
            {article.performance_metrics?.comments > 0 && (
              <div className="flex items-center gap-1">
                <MessageCircle className="w-4 h-4" />
                <span className="text-xs font-bold">
                  {article.performance_metrics.comments}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Read More */}
        <button className="mt-4 w-full bg-gradient-to-r from-[#00ff88] to-[#00d9ff] text-[#0a0a0b] font-bold uppercase text-sm py-3 rounded-lg hover:scale-105 transition-transform">
          Read The Hype →
        </button>
      </div>
    </div>
  );
};

const Flame = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z"/>
  </svg>
);

export default HypeArticleCard;
